// @flow strict

import React, { type Node } from 'react';

import * as styles from './bullet-list.module.less';

// type of component props
type ListItemProps = {
  children: Node,
  url?: string,
  number?: string | null,
};

// type of list component props
type ListProps = {
  numbers?: string,
  children: React.ChildrenArray<React.Element<typeof BulletListItem> | string>,
};

/**
 * Component to render content of list item.
 *
 * @param props - component props
 *
 * @returns {Node}
 */
const ListItemContent = ({ children, url }: ListItemProps): Node => {
  // if url is given, wrap children inside an anchor tag.
  return url == null ? (
    children
  ) : (
    <a href={url} target="_self">
      {children}
    </a>
  );
};

/**
 * Renders list item of bullet list.
 * Includes content as text or link & if number
 * is passed, render bullet with number.
 *
 * @param props - component props
 *
 * @returns {Node}
 */
export const BulletListItem = ({
  children,
  url,
  number = '',
}: ListItemProps): Node => {
  return (
    <li className={styles.listItem}>
      <span>{number}</span>
      <ListItemContent url={url}>{children}</ListItemContent>
    </li>
  );
};

/**
 * Bullet list component.
 * Renders bullet list items, children of other types are
 * being ignored.
 *
 * @param props - component props
 *
 * @returns {Node}
 */
export const BulletList = ({
  numbers: showNumbers,
  children,
}: ListProps): Node => {
  // if there are no children, or children is not an array
  // do nothing
  if (children == null || children.length === 0) {
    return;
  }

  // filter for BulletListItems
  // and add bullet number to each element
  const listItems = children
    .filter((item) => React.isValidElement(item))
    .map((listItem, idx) =>
      React.cloneElement(listItem, {
        number: showNumbers != null ? idx + 1 : null,
      }),
    );

  // render list wrapper & list with items
  return (
    <div className={`${styles.markdownComponent} ${styles.bulletList}`}>
      <ul className={styles.list}>{listItems}</ul>
    </div>
  );
};
